import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  CommentsBySectionIdResponse,
  getReportComments,
  toUserType,
  Answer,
} from './api';
import { SelectedCommentSection } from './SectionButtons';
import {
  FeedbackCommentProps,
  FeedbackComments,
  FeedbackSection,
  UserType,
} from './utils';
import SectionCommentRow from './SectionCommentRow';
import {
  disabledFeedbackQuestions,
  getQuestionText,
} from 'admin/pages/ManageAccounts/Guidance/disabledQuestions';
import { CustomQuestion } from 'admin/pages/ManageAccounts/CustomQuestions/utilts';
import FeedbackCustomQuestions from './FeedbackCustomQuestions';
import { INDICATOR_SUMMARY_QUESTION_ID } from 'npo/pages/NPOFeedback/utils';
import { FeedbackApi } from '../api';

const StyledTrueImpactText = styled(TrueImpactText)`
  padding-left: 40px;
  padding-right: 40px;
  padding-bottom: 40px;
`;

interface FeedbackCommentsBySectionProps {
  reportId: number;
  userName: string;
  report: FeedbackApi;
  className?: string;
  userType: UserType;
  isReportInThePassed: boolean;
  currentSection: FeedbackSection;
  currentUserId: string | undefined;
  currentUserEmail: string | undefined;
  customQuestions: CustomQuestion[];
  selectedCommentSection: SelectedCommentSection;
  answers: Answer[];
  isError: boolean;
  isLoading: boolean;
  reloadReportAnswers?: () => unknown;
  setTotalComments: React.Dispatch<React.SetStateAction<number>>;
}

const FeedbackCommentsBySection: React.FC<FeedbackCommentsBySectionProps> = ({
  reportId,
  userName,
  report,
  className,
  userType,
  currentSection,
  customQuestions,
  currentUserEmail,
  selectedCommentSection,
  answers,
  isLoading,
  isError,
  setTotalComments,
  reloadReportAnswers,
}) => {
  const client = useClient();
  const [comments, setComments] = useState<FeedbackComments | undefined>(
    undefined,
  );
  const [getCommentsRequestState, makeGetCommentsRequest] = useGraphQLRequest<
    CommentsBySectionIdResponse
  >();

  const addOrUpdateComment = useCallback(
    (
      questionId: number,
      comment: FeedbackCommentProps,
      index: number | undefined,
    ) => {
      setComments(s => {
        const newState = { ...s };
        if (!newState[questionId]) {
          setTotalComments(s => s + 1);
          newState[questionId] = [comment];
        } else if (index === undefined) {
          setTotalComments(s => s + 1);
          newState[questionId]!.push(comment);
        } else {
          newState[questionId]![index] = comment;
        }
        return newState;
      });
    },
    [setComments],
  );

  const deleteComment = useCallback(
    (questionId: number, index: number) => {
      setComments(s => {
        const newState = { ...s };
        newState[questionId]?.splice(index, 1);
        return newState;
      });
      setTotalComments(s => s - 1);
    },
    [setComments, setTotalComments],
  );

  useEffect(() => {
    makeGetCommentsRequest(
      getReportComments(client!, reportId, currentSection.sectionId),
    );
  }, [reportId, currentSection.sectionId]);

  useEffect(() => {
    if (getCommentsRequestState.type !== 'REQUEST_SUCCESS') return;
    const result: FeedbackComments = {};
    setTotalComments(getCommentsRequestState.data.allNpoReportComments.length);
    for (const comment of getCommentsRequestState.data.allNpoReportComments) {
      const questionId = comment.questionId || comment.surveyQuestionId;
      if (!questionId) continue;
      const c: FeedbackCommentProps = {
        commentId: comment.commentId,
        details: comment.details,
        text: comment.comment,
        email: comment.userEmail,
        userId: comment.userId,
        accountType: toUserType(comment.userType),
        name: comment.userName,
        createdAt: new Date().toDateString(),
      };
      const commentArray = result[questionId];
      if (commentArray) {
        commentArray.push(c);
      } else {
        result[questionId] = [c];
      }
    }
    setComments(result);
  }, [getCommentsRequestState, setComments]);

  if (
    isLoading ||
    comments === undefined ||
    getCommentsRequestState.type === 'REQUEST_INIT' ||
    getCommentsRequestState.type === 'REQUEST_START'
  ) {
    return <TrueImpactLoading />;
  }

  if (isError || getCommentsRequestState.type !== 'REQUEST_SUCCESS') {
    return (
      <StyledTrueImpactText color="critical">
        Unable to load comments for this section.
      </StyledTrueImpactText>
    );
  }

  if (answers.length === 0) {
    if (
      currentSection.sectionId !== 'custom_questions' ||
      customQuestions.length === 0
    ) {
      return (
        <StyledTrueImpactText>
          No questions were answered in this section.
        </StyledTrueImpactText>
      );
    }
  }

  if (currentSection.sectionId === 'custom_questions') {
    return (
      <FeedbackCustomQuestions
        addOrUpdateComment={addOrUpdateComment}
        deleteComment={deleteComment}
        report={report}
        userName={userName}
        userType={userType}
        reportId={reportId}
        currentUserEmail={currentUserEmail}
        customQuestions={customQuestions}
        feedbackSectionId="custom_questions"
        selectedCommentSection={selectedCommentSection}
        comments={comments}
        allAnswers={answers}
      />
    );
  }

  return (
    <div className={className}>
      {answers.map(answer => {
        if (
          disabledFeedbackQuestions[currentSection.sectionId][
            answer.question.dbId
          ] ||
          (currentSection.sectionId === 'indicator_details' &&
            answer.question.dbId !== INDICATOR_SUMMARY_QUESTION_ID)
        ) {
          return null;
        }

        return (
          <SectionCommentRow
            answer={answer}
            allAnswers={answers}
            userName={userName}
            key={answer.dbId}
            report={report}
            questionId={answer.question.dbId}
            reportId={reportId}
            addOrUpdateComment={addOrUpdateComment}
            deleteComment={deleteComment}
            reloadReportAnswers={reloadReportAnswers}
            comments={comments[answer.question.dbId] || []}
            feedbackSectionId={currentSection.sectionId}
            title={getQuestionText(
              answer.question.dbId,
              answer.questionText || answer.question.text,
            )}
            currentUserEmail={currentUserEmail}
            userType={userType}
            selectedCommentSection={selectedCommentSection}
          />
        );
      })}
    </div>
  );
};

export default FeedbackCommentsBySection;
